import React from "react";
import moment from 'moment';
import PropTypes from 'prop-types';

import DateTimePicker from '@components/common/date_time_picker/main';
import Checkbox from '@components/common/checkbox/main';
import { BFLoader } from '@components/common/loader/main';
import { pluralizeObject, Brandfolder } from '@helpers/show_page_helpers';
import renderModal from '../modals/renderModal';

import './styles/BulkCollectionModal.scss';

class BulkCollectionModal extends React.Component {
  state = {
    adminable: BF.fx.role() === 'admin' || BF.fx.role() === 'owner' || BFG.currentUser.su,
    allSelected: false,
    availabilitySet: false,
    availability_start_date: moment().format('MM/DD/YYYY'),
    availability_start_time: '12:01 am',
    availability_start_zone: moment.tz.guess(),
    availability_end_date: moment().add(7, 'days').format('MM/DD/YYYY'),
    availability_end_time: '11:59 pm',
    availability_end_zone: moment.tz.guess(),
    collectionSearchValue: '',
    disableSubmitButton: true,
    displayCollectionSearch: false,
    existingCollections: [],
    isLoading: true,
    new_collection_name: '',
    newCollection: false,
    selectedCollections: []
  }

  componentDidMount() {
    this.getCollections();
  }

  componentWillUnmount() {
    this.props.closeModal();
  }

  getCollections() {
    $.ajax({
      type: "GET",
      url: `/api/v4/brandfolders/${Brandfolder.key}/collections?fields=parent_id&per=1000&queue_priority=high`,
      contentType: "application/json",
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        if (!response.data.length) {
          this.setState({ newCollection: true, existingCollections: [] });
        } else {
          let collectionData;
          if (BFG?.resource?.type === "collection") {
            // render only subcollections for the parent (current resource)
            collectionData = response.data.filter((collection) => (
              collection?.attributes?.parent_id === BFG.resource.key));
          } else {
            // render only parent (top-level) collections
            collectionData = response.data.filter((collection) => (
              collection?.attributes?.parent_id === null));
          }
          this.setState({
            existingCollections: collectionData,
            displayCollectionSearch: response.data.length >= 10
          });
        }
      },
      error: () => {
      },
      complete: () => {
        this.setState({ isLoading: false });
      }
    });
  }

  availability_start = () => {
    const { availability_start_date, availability_start_time } = this.state;
    let { availability_start_zone } = this.state;
    availability_start_zone = availability_start_zone || moment.tz.guess();

    return moment.tz(`${availability_start_date} ${availability_start_time}`, 'MM/DD/YYYY hh:mm a', availability_start_zone);
  }

  availability_end = () => {
    const { availability_end_date, availability_end_time } = this.state;
    let { availability_end_zone } = this.state;
    availability_end_zone = availability_end_zone || moment.tz.guess();

    return moment.tz(`${availability_end_date} ${availability_end_time}`, 'MM/DD/YYYY hh:mm a', availability_end_zone);
  }

  buildDataBody = () => {
    const { selectedAssetKeys } = this.props;
    const { availabilitySet, selectedCollections, new_collection_name } = this.state;
    const selectedCollectionKeys = selectedCollections.map((collection) => collection.id);

    return {
      data: {
        asset_keys: Array.from(selectedAssetKeys),
        brandfolder_key: Brandfolder.key,
        collection_keys: selectedCollectionKeys,
        new_collection_name,
        collection_asset_associations: {
          availability_start: (availabilitySet ? this.availability_start().format() : null),
          availability_end: (availabilitySet ? this.availability_end().format() : null)
        }
      }
    };
  }

  createCollectionAssociations = () => {
    this.setState({ disableSubmitButton: true, isLoading: true });

    const url = '/api/v4/bulk_actions/assets/collection_associations?queue_priority=high';
    const { newCollection, selectedCollections, new_collection_name } = this.state;
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');
    const collectionsQtyObject = pluralizeObject(selectedCollections);
    const collectionCopy = collectionsQtyObject.plural ? t('collections') : t('collection');

    $.ajax({
      type: "POST",
      url,
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        this.props.closeModal();
        const body = this.state.availabilitySet ? t('Assets may not appear immediately due to selected publish/expiration date(s).') : '';
        if (newCollection) {
          BF.fx.dispatchWindowEvent('newCollectionAdded', null, { newCollection: response.data[0].attributes });
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${t('added to')} ${new_collection_name}!`,
            body,
            type: 'success'
          });
        } else if (selectedCollections.length === 1) {
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${t('added to')} ${selectedCollections[0].attributes.name}!`,
            body,
            type: 'success'
          });
        } else {
          Notify.create({
            title: `${assetQtyObject.qty} ${assetCopy} ${t('added to')} ${collectionsQtyObject.qty} ${collectionCopy}!`,
            body,
            type: 'success'
          });
        }
      },
      error: (xhr) => {
        const errorText = xhr.responseText;
        if (errorText.indexOf('Insufficient Plan') > -1) {
          this.props.closeModal();
          Notify.create({
            title: t('Please upgrade to add more collections.'),
            type: 'error'
          });
        } else {
          this.setState({ disableSubmitButton: false, isLoading: false });
          Notify.create({
            title: `${t('An error occurred when adding')} ${assetQtyObject.qty} ${assetCopy} ${t('to')} ${collectionsQtyObject.qty} ${collectionCopy}. ${t('Please try again.')}`,
            type: 'error'
          });
        }
      }
    });
  }

  enableActionButton = () => {
    const { availabilitySet, selectedCollections, newCollection, new_collection_name } = this.state;
    let enabled = true;

    // validate publish and expiration dates
    if (availabilitySet) {
      if (this.availability_start().isValid() && this.availability_end().isValid()) {
        enabled = this.availability_start().isBefore(this.availability_end()); // is expire date after than publish date
      } else {
        enabled = false;
      }
    }

    // validate collection selections if dates are validated
    if (enabled) {
      if (selectedCollections.length || (newCollection === true && new_collection_name !== '')) {
        enabled = true;
      } else {
        enabled = false;
      }
    }

    this.setState({ disableSubmitButton: !enabled });
  }

  updateDateTimeStart = (availability_start_date, availability_start_time, availability_start_zone) => {
    this.setState({
      availability_start_date,
      availability_start_time,
      availability_start_zone
    }, () => this.enableActionButton());
  }

  updateDateTimeEnd = (availability_end_date, availability_end_time, availability_end_zone) => {
    this.setState({
      availability_end_date,
      availability_end_time,
      availability_end_zone
    }, () => this.enableActionButton());
  }

  toggleNewCollection() {
    if (this.state.newCollection) {
      this.setState((prevState) => ({
        newCollection: false,
        displayCollectionSearch: prevState.existingCollections.length >= 10,
        new_collection_name: '',
        allSelected: false
      }), () => this.enableActionButton());
    } else {
      this.setState({
        newCollection: true,
        displayCollectionSearch: false,
        selectedCollections: []
      }, () => this.enableActionButton());
    }
  }

  toggleExpireAssets() {
    if (this.state.availabilitySet) {
      this.setState({ availabilitySet: false }, () => this.enableActionButton());
    } else {
      this.setState({ availabilitySet: true }, () => this.enableActionButton());
    }
  }

  updateAllCollectionsCheckbox() {
    if (this.state.allSelected) {
      this.setState({
        selectedCollections: [],
        allSelected: false
      }, () => this.enableActionButton());
    } else {
      this.setState((prevState) => (
        {
          selectedCollections: [...prevState.existingCollections],
          allSelected: true
        }
      ), () => this.enableActionButton());
    }
  }

  updateSelectedCollections(collectionId) {
    this.setState((prevState) => {
      const existingCollections = [...prevState.existingCollections];
      const selectedCollections = [...prevState.selectedCollections];
      let collectionClicked = false;

      for (let i = 0; i < existingCollections.length; i += 1) {
        if (existingCollections[i].id === collectionId) {
          collectionClicked = existingCollections[i];
        }
      }

      const selectedIndex = selectedCollections.indexOf(collectionClicked);

      if (selectedIndex > -1) {
        selectedCollections.splice(selectedIndex, 1);
      } else {
        selectedCollections.push(collectionClicked);
      }

      const allSelected = selectedCollections.length === existingCollections.length;

      return { selectedCollections, existingCollections, allSelected };
    }, () => this.enableActionButton());
  }

  checkboxifyExistingCollections() {
    const { existingCollections, selectedCollections, collectionSearchValue } = this.state;
    const existingCollectionsSorted = [...existingCollections].sort((a, b) => {
      if (a.attributes.name.toUpperCase() > b.attributes.name.toUpperCase()) {
        return 1;
      }
      if (b.attributes.name.toUpperCase() > a.attributes.name.toUpperCase()) {
        return -1;
      }
      return 0;
    });
    const displayedCollections = [];

    for (let i = 0; i < existingCollectionsSorted.length; i += 1) {
      const collection = existingCollectionsSorted[i];
      const collectionName = collection.attributes.name;
      const collectionId = collection.id;
      const displayCollection = collectionName.toUpperCase().indexOf(collectionSearchValue.toUpperCase()) > -1;

      if (displayCollection) {
        displayedCollections.push(
          <div className="checkbox-container" key={i}>
            <Checkbox
              checked={selectedCollections.indexOf(collection) > -1}
              labelHtml={<h5> {collectionName} </h5>}
              size="sm"
              toggle={() => this.updateSelectedCollections(collectionId)}
            />
          </div>
        );
      }
    }

    return displayedCollections;
  }

  resourceReference() {
    const collectionContext = BFG?.resource?.type === "collection";
    return collectionContext ? "Subcollection" : "Collection";
  }

  renderRowTitle() {
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const { adminable, existingCollections } = this.state;

    if (!adminable && !existingCollections.length) { return null; }

    return (
      <>
        <span className="bold-text">
          {assetQtyObject.qty} {t('asset')}{assetQtyObject.plural}
        </span>
        {existingCollections.length ? `${t('to one or more')} ${this.resourceReference()}s` : t('to a new Collection')}
      </>
    );
  }

  renderRadioButtons() {
    if (!this.state.adminable || !this.state.existingCollections.length || BFG?.resource?.type === "collection") {
      return null;
    }
    return (
      <div className="radio-button-container">
        <label className="radio-row">
          <h4>{`${t('Add to existing')} Collection(s)`}</h4>
          <input
            className="radio-input"
            type="radio"
            name="radio"
            value="add-to-existing-collection"
            checked={!this.state.newCollection}
            onChange={() => this.toggleNewCollection()}
          />
          <span className="radio-dot" />
        </label>
        <label className="radio-row">
          <h4>{t('Create new Collection')}</h4>
          <input
            className="radio-input"
            type="radio"
            name="radio"
            value="add-to-new-collection"
            checked={this.state.newCollection}
            onChange={() => this.toggleNewCollection()}
          />
          <span className="radio-dot" />
        </label>
      </div>
    );
  }

  renderCollectionSearch() {
    if (!this.state.displayCollectionSearch) { return null; }

    const hideCloseClass = this.state.collectionSearchValue === '' ? 'hide-close' : '';

    return (
      <div className="add-collection-search-container">
        <span className="icon bff-search" />
        <span
          className={`icon bff-close ${hideCloseClass}`}
          onClick={() => this.setState({ collectionSearchValue: '' })}
        />
        <input
          className="inputs add-collection-search"
          type="text"
          value={this.state.collectionSearchValue}
          onChange={(e) => { this.setState({ collectionSearchValue: e.target.value }); }}
          placeholder={t("Search collections")}
        />
      </div>
    );
  }

  renderCollectionList() {
    if (!this.state.existingCollections.length || this.state.newCollection) { return null; }

    const containerClass = this.state.displayCollectionSearch ? 'fixed-height' : '';
    const selectAllCollectionsCheckbox = (
      <div className="checkbox-container" key="-1">
        <Checkbox
          size="sm"
          checked={this.state.allSelected}
          toggle={() => this.updateAllCollectionsCheckbox()}
          labelHtml={<h5> {`${t('All')} ${this.resourceReference()}s`}</h5>}
        />
      </div>
    );

    return (
      <div className={`collection-list-container ${containerClass}`}>
        { [selectAllCollectionsCheckbox, this.checkboxifyExistingCollections()] }
      </div>
    );
  }

  renderNewCollectionInput() {
    const { adminable, newCollection } = this.state;
    if (!adminable || !newCollection) { return null; }

    return (
      <div className="collection-selector-container">
        <label className="brandfolder-label new-resource-label">
          {t("Collection Name")}
          <input
            className="inputs"
            type="text"
            onChange={(e) => {
              this.setState({ new_collection_name: e.target.value }, () => this.enableActionButton());
            }}
          />
        </label>
      </div>
    );
  }

  renderNoAccess() {
    if (!this.state.adminable && !this.state.existingCollections.length) {
      return <h4 className="no-collections-copy">{t('It looks like this Brandfolder has no collections. Contact an administrator to create a Collection.')}</h4>;
    }

    return null;
  }

  renderAvailabilitySelector() {
    if (!this.state.availabilitySet) { return null; }

    const {
      availability_start_date,
      availability_start_time,
      availability_start_zone,
      availability_end_date,
      availability_end_time,
      availability_end_zone } = this.state;

    return (
      <div className="expiration-container">
        <h4 className="row-title">{t('Set Publish Date (hide from collection')}
          <span className="bold-text">{t('until')}</span>
          {t('this date)')}
        </h4>
        <DateTimePicker
          selectedDate={availability_start_date}
          selectedTime={availability_start_time}
          selectedZone={availability_start_zone}
          updateDateTime={this.updateDateTimeStart}
        />
        <div className="horiz-line" />
        <h4 className="row-title">{t('Set Expiration Date (remove from collection')}
          <span className="bold-text">{t('after')}</span>
          {t('this date)')}
        </h4>
        <DateTimePicker
          selectedDate={availability_end_date}
          selectedTime={availability_end_time}
          selectedZone={availability_end_zone}
          updateDateTime={this.updateDateTimeEnd}
        />
      </div>
    );
  }

  render() {
    const {
      adminable,
      availabilitySet,
      disableSubmitButton,
      existingCollections,
      isLoading
    } = this.state;
    const { closeModal } = this.props;
    const loaderActive = isLoading ? 'loader-active' : '';

    return (
      <div className="modal-content-wrapper bulk-collection-modal">
        <div className="modal-content-wrapper__header">
          <span className="bff-plus icon" aria-hidden="true" />
          <h3 className="modal-title">{t('Add')}
            {this.renderRowTitle()}
          </h3>
          <button
            className="close-button"
            onClick={closeModal}
            type="button"
          >
            <span className="bff-close" />
          </button>
        </div>
        <div className={`modal-content-wrapper__body ${loaderActive}`}>
          {isLoading
            ? <BFLoader />
            : (
              <div>
                <div className="modal-row">
                  { this.renderRadioButtons() }
                  { this.renderCollectionSearch() }
                  { this.renderCollectionList() }
                  { this.renderNewCollectionInput() }
                  { this.renderNoAccess() }
                </div>
                {(adminable || existingCollections.length !== 0) && (
                  <React.Fragment>
                    <div className="horiz-line" />
                    <div className="modal-row">
                      <div className="checkbox-container">
                        <Checkbox
                          size="sm"
                          checked={availabilitySet}
                          toggle={() => this.toggleExpireAssets()}
                          labelHtml={<h5> {`${t('Set Asset Availability For')} ${this.resourceReference()}s`} </h5>}
                        />
                      </div>
                      { this.renderAvailabilitySelector() }
                    </div>
                  </React.Fragment>
                )}
                <div className="button-container">
                  <button
                    className="button sm primary t-confirm-modal"
                    disabled={disableSubmitButton}
                    onClick={() => this.createCollectionAssociations()}
                    type="button"
                  >
                    {t('Submit')}
                  </button>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

BulkCollectionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

const ModalComponent = renderModal(BulkCollectionModal, 'BulkCollectionModal');
export default ModalComponent;
